import { action } from "typesafe-actions";
import { IApiKey } from "./apikey.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
    closeLoaderWithMessage,
    openLoaderWithMessage,
    showMessage,
} from "../messages/messagesActions";
import { ISearchQueryParams } from "../common/common.types";
import { addSearchFilterToAdvanceFilter, getSearchQueryV2 } from "../common/helpers";
import { endPoints } from "../../constants/endPoints";

/* =====================================================
   FETCH API KEY LIST ACTIONS
===================================================== */

export const FETCH_APIKEY_LIST_PROGRESS = "FETCH_APIKEY_LIST_PROGRESS";
export const FETCH_APIKEY_LIST_SUCCESS = "FETCH_APIKEY_LIST_SUCCESS";
export const FETCH_APIKEY_LIST_FAILED = "FETCH_APIKEY_LIST_FAILED";

export const fetchApiKeyListProgress = () =>
    action(FETCH_APIKEY_LIST_PROGRESS);

export const fetchApiKeyListSuccess = (list: IApiKey[], totalRecords: number) =>
    action(FETCH_APIKEY_LIST_SUCCESS, { list, totalRecords });

export const fetchApiKeyListFailed = () =>
    action(FETCH_APIKEY_LIST_FAILED);

export const fetchApiKeyListAsync = (
    queryParams: ISearchQueryParams
): ThunkAction<void, IStoreState, {}, AnyAction> => async (dispatch) => {
    try {
        dispatch(fetchApiKeyListProgress());

        const {
            page,
            rowsPerPage,
            status,
            date,
            advancedFilters,
            apikey_name,
            apikey_source,
            companyOutletUuid
        } = queryParams as any;
        const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

        addSearchFilterToAdvanceFilter(advanceFilter, ["apikey_name"], apikey_name);
        addSearchFilterToAdvanceFilter(advanceFilter, ["apikey_source"], apikey_source);

        let finalUrl = `${endPoints.apikey.getApiKey}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

        if (date?.fromDate && date?.toDate) {
            finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        if (status && status !== "-1") {
            finalUrl += `&status=${status}`;
        }

        if (companyOutletUuid) {
            finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
        }

        if (advanceFilter.length > 0) {
            finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        const res = await api.get(finalUrl);

        const data: IApiKey[] = res.data.data || [];
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchApiKeyListSuccess(data, totalRecords));
    } catch (err: any) {
        dispatch(fetchApiKeyListFailed());
        dispatch(
            showMessage({
                message: err?.response?.data?.message || "Failed to fetch API keys",
                type: "error",
                displayAs: "snackbar",
            })
        );
    }
};
/* =====================================================
   FETCH SINGLE API KEY ACTIONS
===================================================== */

export const FETCH_APIKEY_PROGRESS = "FETCH_APIKEY_PROGRESS";
export const FETCH_APIKEY_SUCCESS = "FETCH_APIKEY_SUCCESS";
export const FETCH_APIKEY_FAILED = "FETCH_APIKEY_FAILED";

export const fetchApiKeyProgress = () =>
    action(FETCH_APIKEY_PROGRESS);

export const fetchApiKeySuccess = (apiKey: IApiKey) =>
    action(FETCH_APIKEY_SUCCESS, { apiKey });

export const fetchApiKeyFailed = (errorMessage: string) =>
    action(FETCH_APIKEY_FAILED, { errorMessage });

export const fetchApiKeyAsync =
    (apikeyUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(fetchApiKeyProgress());

                const res = await api.get(
                    `${endPoints.apikey.getApiKey}?apikey_uuid=${apikeyUuid}`
                );

                const data: IApiKey = res.data.data[0];

                dispatch(fetchApiKeySuccess(data));
            } catch (err: any) {
                dispatch(
                    fetchApiKeyFailed(
                        err?.response?.data?.message ||
                        "Failed to fetch API key"
                    )
                );
                dispatch(
                    showMessage({
                        message:
                            err?.response?.data?.message ||
                            "Failed to fetch API key",
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
            }
        };

/* =====================================================
   UPSERT API KEY ACTIONS
===================================================== */

export const upsertApiKeyAsync =
    (
        apiKeyData: IApiKey,
        onCallBack: (isSuccess?: boolean, data?: IApiKey) => void
    ): ThunkAction<void, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(
                    openLoaderWithMessage("Saving API key, please wait...")
                );

                const {
                    create_ts,
                    insert_ts,
                    created_by_name,
                    created_by_uuid,
                    modified_by_name,
                    modified_by_uuid,
                    ...payload
                } = apiKeyData;

                const res = await api.post(
                    endPoints.apikey.upsertApiKey,
                    payload
                );

                const data: IApiKey = res.data.data;

                dispatch(
                    showMessage({
                        message: res.data.message,
                        type: "success",
                        displayAs: "snackbar",
                    })
                );

                onCallBack(true, data);
            } catch (err: any) {
                dispatch(
                    showMessage({
                        message:
                            err?.response?.data?.message ||
                            "Failed to save API key",
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
                onCallBack(false);
            } finally {
                dispatch(closeLoaderWithMessage());
            }
        };

/* =====================================================
   CLEAR ACTIONS
===================================================== */

export const CLEAR_APIKEY_STATE = "CLEAR_APIKEY_STATE";
export const CLEAR_APIKEY = "CLEAR_APIKEY";

export const clearApiKeyState = () =>
    action(CLEAR_APIKEY_STATE);

export const clearApiKey = () =>
    action(CLEAR_APIKEY);
